import { useState } from "react";
import { DetailDrawer } from "@/components/ui/detail-drawer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, Check, Download, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";
import {
  useRefreshInstallerUrl,
  useRevokeInstaller,
} from "@/hooks/security/useEdrInstallers";
import { MdmSnippetTabs } from "./MdmSnippetTabs";
import { formatBytes, installerFilename } from "./installerCopy";
import type { InstallerBuild } from "@/types/edr-installers";

export function InstallerDetailDrawer({
  build,
  onOpenChange,
  onUpdate,
}: {
  build: InstallerBuild | null;
  onOpenChange: (o: boolean) => void;
  onUpdate: (b: InstallerBuild) => void;
}) {
  const refresh = useRefreshInstallerUrl();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  if (!build) {
    return (
      <DetailDrawer open={false} onOpenChange={onOpenChange} title="Installer">
        <span />
      </DetailDrawer>
    );
  }

  const tokenDaysLeft = differenceInDays(new Date(build.tokenExpiresAt), new Date());
  const urlExpired = new Date(build.downloadUrlExpiresAt).getTime() < Date.now();

  const expiryPill =
    tokenDaysLeft < 0
      ? "bg-destructive/10 text-destructive border-destructive/30"
      : tokenDaysLeft < 14
        ? "bg-warning-muted text-warning-muted-foreground border-warning-border"
        : "bg-success-muted text-success-muted-foreground border-success-border";

  const handleRefresh = async () => {
    try {
      const res = await refresh.mutateAsync(build.id);
      onUpdate({ ...build, ...res });
      toast.success("Download URL refreshed");
    } catch {
      // toast handled by hook
    }
  };

  return (
    <>
      <DetailDrawer
        open={!!build}
        onOpenChange={onOpenChange}
        title={`${build.tagName} · ${build.platform}`}
        description={
          <span
            className={cn(
              "inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold",
              expiryPill,
            )}
          >
            {tokenDaysLeft < 0
              ? "Token expired"
              : `Token expires in ${tokenDaysLeft}d`}
          </span>
        }
        footer={
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Revoking blocks new installs. Already-enrolled sensors keep working.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmRevoke(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Revoke installer
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <Section label="Download">
            <Row k="Filename" v={<code className="font-mono text-xs">{installerFilename(build)}</code>} />
            <Row k="Size" v={formatBytes(build.size)} />
            <Row k="Version" v={build.version} />
            <Row k="Channel" v={build.channel} />
            <Row
              k="SHA-256"
              v={
                <span className="flex items-center gap-1">
                  <code className="font-mono text-[10px] break-all">{build.sha256}</code>
                  <CopyIconBtn value={build.sha256} />
                </span>
              }
            />
            <Row
              k="URL expires"
              v={
                <span className={urlExpired ? "text-destructive" : undefined}>
                  {formatDistanceToNow(new Date(build.downloadUrlExpiresAt), { addSuffix: true })}
                </span>
              }
            />
            <div className="px-3 py-2.5 flex gap-2">
              {urlExpired ? (
                <Button
                  className="flex-1"
                  onClick={handleRefresh}
                  disabled={refresh.isPending}
                >
                  <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", refresh.isPending && "animate-spin")} />
                  Refresh download URL
                </Button>
              ) : (
                <Button asChild className="flex-1">
                  <a href={build.downloadUrl} target="_blank" rel="noreferrer">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Download installer
                  </a>
                </Button>
              )}
              <CopyTextBtn label="Copy URL" value={build.downloadUrl} />
            </div>
          </Section>

          <Section label="Distribution">
            <div className="p-3">
              <MdmSnippetTabs build={build} />
            </div>
          </Section>

          <Section label="Identity">
            {build.tenantName ? <Row k="Tenant" v={build.tenantName} /> : null}
            <Row k="Tag" v={build.tagName} />
            <Row k="Tag ID" v={<code className="font-mono text-xs">{build.tagId}</code>} />
            <Row k="Installer ID" v={<code className="font-mono text-xs">{build.id}</code>} />
            <Row
              k="Token expires"
              v={formatDistanceToNow(new Date(build.tokenExpiresAt), { addSuffix: true })}
            />
            <Row
              k="Created"
              v={`${formatDistanceToNow(new Date(build.createdAt), { addSuffix: true })} by ${build.createdBy}`}
            />
            <Row k="Enrolled sensors" v={String(build.enrolledCount)} />
            <Row
              k="Last enrolled"
              v={
                build.lastEnrolledAt
                  ? formatDistanceToNow(new Date(build.lastEnrolledAt), { addSuffix: true })
                  : "—"
              }
            />
          </Section>
        </div>
      </DetailDrawer>

      <RevokeDialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        installerId={build.id}
        tagName={build.tagName}
        onRevoked={() => {
          setConfirmRevoke(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}

function RevokeDialog({
  open,
  onOpenChange,
  installerId,
  tagName,
  onRevoked,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  installerId: string;
  tagName: string;
  onRevoked: () => void;
}) {
  const revoke = useRevokeInstaller();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke installer?</DialogTitle>
          <DialogDescription>
            Future installs from this artifact for <strong>{tagName}</strong> will
            fail. Sensors that already enrolled with this installer keep working
            — they swapped the install token for a per-device agent identity on
            first check-in.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={revoke.isPending}
            onClick={async () => {
              try {
                await revoke.mutateAsync(installerId);
                onRevoked();
              } catch {
                // hook toasts
              }
            }}
          >
            {revoke.isPending ? "Revoking…" : "Revoke"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {label}
      </h3>
      <div className="rounded border border-border divide-y divide-border">{children}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2 text-xs">
      <span className="text-muted-foreground shrink-0">{k}</span>
      <span className="text-foreground text-right break-all">{v ?? "—"}</span>
    </div>
  );
}

function CopyIconBtn({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-6 w-6 p-0"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          toast.success("Copied");
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  );
}

function CopyTextBtn({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          toast.success("Copied");
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
      {label}
    </Button>
  );
}
